"use client";
import React, { useState } from "react";
import { FeaturedCity, CityCmsData, City, TourCategory } from "@/data/types";
import { Route } from "@/routers/types";
import { useAppSelector, useAppDispatch } from '@/app/lib/hooks';
import { setTourCategories } from '@/app/lib/reducers/tourCategoriesReducer';
import { setFeaturedCities } from '@/app/lib/reducers/citiesReducer';
import { useTranslation } from 'react-i18next';
import DesktopHomeRedesign from "./(home)/desktop/DesktopHomeRedesign";
import MobileLanding from "./(home)/MobileLanding";

// Desktop home is SSR-rendered (used to be `dynamic({ ssr: false })`, which
// hid the hero from first paint and produced LCP ~4 s + CLS ~0.45). All the
// individual sections downstream are still code-split + deferred via
// `<DeferUntilVisible>` so the JS bundle stays lean; only the SSR HTML is
// emitted up front for the chrome + hero. Each section component is written
// SSR-safe (no Date.now/Math.random in render, empty-Redux fallback paths).

interface Props {
  initialCategories: TourCategory[];
  initialFeaturedCities: City[];
  initialLiveTravellersCount: number;
}

function HomePageClient({
  initialCategories,
  initialFeaturedCities,
  initialLiveTravellersCount,
}: Props) {
  const dispatch = useAppDispatch();
  // Prefer Redux state once thunks have replenished it (e.g. on language
  // switch). Fall back to the SSR-provided props so the very first paint
  // already has data without waiting on hydration of the thunks.
  const reduxCategories: TourCategory[] = useAppSelector(state => state.tourCategories.tourCategories);
  const reduxCities: City[] = useAppSelector(state => state.cities.featured);
  const { t } = useTranslation("guydeez");

  // Synchronously hydrate Redux from SSR data BEFORE first render of children.
  useState(() => {
    if (initialCategories?.length) dispatch(setTourCategories(initialCategories));
    if (initialFeaturedCities?.length) dispatch(setFeaturedCities(initialFeaturedCities));
  });

  const tourCategories = reduxCategories?.length ? reduxCategories : initialCategories;
  const cities = reduxCities?.length ? reduxCities : initialFeaturedCities;

  const mappedCities: FeaturedCity[] = cities.map(city => {
    const entryData: CityCmsData = city.entry.data.length > 0 ? city.entry.data[0] : {
      mainImage: 'default-thumbnail-url',
      short_content: ''
    } as CityCmsData;
    return {
      id: city.id.toString(),
      href: `/city/${city.slug}` as Route<string>,
      name: city.name,
      taxonomy: "category",
      totalTours: city.totalTours,
      avgRating: city.avgRating,
      thumbnail: entryData.mainImage || "default-thumbnail-url",
      entry: {
        data: city.entry.data,
        title: city.entry.title || '',
        short_content: city.entry.short_content || '',
        content: city.entry.content || '',
        galleries: city.entry.galleries || [],
        youtube: city.entry.youtube || []
      },
      // Forward the joined country so MobileLanding can show the flag +
      // country name pill on each carousel card.
      countryInfo: city.countryInfo
        ? {
            code: city.countryInfo.code,
            name: city.countryInfo.name,
            // Pre-resolve the localized country name from the joined CMS
            // entry (e.g. "Frankreich" for FR + de_DE). MobileLanding only
            // gets this slimmed countryInfo, not the full entry payload,
            // so we compute the display label once here.
            nameTranslated:
              (city as any).countryInfo?.entry?.data?.[0]?.title ||
              (city as any).countryInfo?.nameTranslated ||
              city.countryInfo.name,
          }
        : undefined,
    };
  });

  return (
    <>
      {/* 💻 Desktop version — 2026 redesign (gated by `hidden lg:block`
          inside the component, untouched by the mobile rewrite). */}
      <DesktopHomeRedesign />

      {/* 📱 Mobile version — 2026 redesign mirroring
          guydeez_claude_design/components/21-mobile-onboarding.jsx. */}
      <MobileLanding
        cities={mappedCities}
        categories={tourCategories}
        liveTravellersCount={initialLiveTravellersCount}
      />
    </>
  );
}

export default HomePageClient;
